// ./src/components/features/WorkflowTable/WorkflowEditDrawer.tsx
import React, { useEffect, useState } from "react";
import { Stack, TextInput, Textarea } from "@mantine/core";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";
import { Workflow, WorkflowInput } from "@/utils/api";
import { AppDrawer, StatusSelector } from "@/components/generic";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// The exact field names sent to and returned by the API.
// Used both to build the submit payload and to extract field-level errors
// from the OpResult via getFormErrors().
const FORM_FIELDS = ["workflowName", "description", "status"] as const;
type FormField = (typeof FORM_FIELDS)[number];

// Form state mirrors WorkflowInput exactly.
// status is kept as number | null — StatusSelector works natively with
// numbers, so there is no string↔number conversion anywhere in this file.
interface FormValues {
  workflowName: string;
  description: string;
  status: number | null;
}

const EMPTY_FORM: FormValues = {
  workflowName: "",
  description: "",
  status: STATUSES.ACTIVE,
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface WorkflowEditDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Called when the drawer should close (X button, Cancel, or backdrop) */
  onClose: () => void;

  /**
   * Workflow to edit. When null/undefined the drawer is in create mode.
   * The form is re-populated whenever this prop changes while the drawer
   * is open (handles the case where data arrives asynchronously after open).
   */
  workflow?: Workflow | null;

  /** Called when the user submits a new workflow */
  onCreateWorkflow: (params: WorkflowInput) => Promise<OpResult>;

  /** Called when the user submits changes to an existing workflow */
  onUpdateWorkflow: (id: string, params: WorkflowInput) => Promise<OpResult>;

  /** True while a create call is in flight */
  isCreating: boolean;

  /** True while an update call is in flight */
  isUpdating: boolean;

  /**
   * The last OpResult from a failed create or update.
   * The drawer extracts field-level errors (shown under each input) and
   * generic errors (shown above the footer via AppDrawer's errors prop).
   * No client-side validation is performed.
   */
  lastError: OpResult | null;

  /** Clears lastError — called on close and whenever the user edits a field */
  onClearError: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const WorkflowEditDrawer: React.FC<WorkflowEditDrawerProps> = ({
  opened,
  onClose,
  workflow,
  onCreateWorkflow,
  onUpdateWorkflow,
  isCreating,
  isUpdating,
  lastError,
  onClearError,
}) => {
  const isEditMode = !!workflow?.id;
  const isBusy = isCreating || isUpdating;

  // ------------------------------------------------------------------
  // Form state — plain controlled inputs, no form library.
  // We deliberately avoid client-side validation: every submit goes
  // straight to the API and we reflect whatever the API says back.
  // ------------------------------------------------------------------
  const [formValues, setFormValues] = useState<FormValues>(EMPTY_FORM);

  // ------------------------------------------------------------------
  // Populate / reset the form when the drawer opens or the workflow prop
  // changes. Three cases:
  //   • Opening in create mode          → reset to empty defaults.
  //   • Opening in edit mode            → populate from the workflow object.
  //   • Workflow arrives asynchronously → re-populate while already open.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!opened) {
      return;
    }

    if (workflow) {
      setFormValues({
        workflowName: workflow.workflowName ?? "",
        description: workflow.description ?? "",
        // status arrives as a number from the API; fall back to ACTIVE if absent
        status: workflow.status ?? STATUSES.ACTIVE,
      });
    } else {
      setFormValues(EMPTY_FORM);
    }

    // Clear any stale errors from the previous session whenever the form
    // is (re-)initialised.
    onClearError();
    // onClearError is a stable useCallback — safe to include in the dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, workflow]);

  // ------------------------------------------------------------------
  // Error extraction
  //
  // OpResult.getFormErrors() splits errors into two buckets:
  //   • fields        — keyed by field name; shown under the relevant input
  //   • genericErrors — field '' or unknown field names; passed to AppDrawer's
  //                     errors prop so they appear above the footer buttons,
  //                     meaning the user never has to scroll up to see them
  // ------------------------------------------------------------------
  const { fields: fieldErrors, genericErrors } = OpResult.getFormErrors(
    lastError,
    FORM_FIELDS as unknown as string[],
  );

  // ------------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------------

  const handleTextChange = (
    field: Extract<FormField, "workflowName" | "description">,
    value: string,
  ) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    // Dismiss errors as soon as the user starts correcting their input
    // so the red underlines don't linger while they are typing.
    if (lastError) {
      onClearError();
    }
  };

  const handleStatusChange = (value: number | null) => {
    setFormValues((prev) => ({ ...prev, status: value }));
    if (lastError) {
      onClearError();
    }
  };

  const handleSubmit = async () => {
    // Build the API payload. status is already a number — no conversion needed.
    // Empty strings are normalised to undefined so the API receives clean data.
    const params: WorkflowInput = {
      workflowName: formValues.workflowName.trim() || undefined,
      description: formValues.description.trim() || undefined,
      status: formValues.status ?? undefined,
    };

    if (isEditMode && workflow?.id) {
      await onUpdateWorkflow(workflow.id, params);
    } else {
      await onCreateWorkflow(params);
    }
    // Do NOT close here — the parent decides whether to close based on
    // result.didSucceed(). Keeping the drawer open on validation errors
    // lets the user read and correct the inline field messages without
    // having to reopen the form.
  };

  const handleClose = () => {
    onClearError();
    onClose();
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <AppDrawer
      opened={opened}
      onClose={handleClose}
      onConfirm={handleSubmit}
      title={isEditMode ? "Edit Workflow" : "New Workflow"}
      actionType="save-cancel"
      size="md"
      position="right"
      // Locks the drawer while a save is in flight: disables backdrop click,
      // Escape key, and the close button via AppDrawer's closable/loading props.
      closable={!isBusy}
      loading={isBusy}
      // Generic / non-field errors are rendered by AppDrawer above the footer
      // so the user sees them immediately without scrolling. Field-level errors
      // are shown inline under each input via the error prop below.
      errors={genericErrors.length > 0 ? genericErrors : undefined}
    >
      <Stack gap="sm">
        <TextInput
          label="Workflow Name"
          placeholder="Enter a unique workflow name"
          value={formValues.workflowName}
          onChange={(e) =>
            handleTextChange("workflowName", e.currentTarget.value)
          }
          error={fieldErrors.workflowName}
          disabled={isBusy}
          autoFocus
        />

        <Textarea
          label="Description"
          placeholder="Describe what this workflow does (optional)"
          value={formValues.description}
          onChange={(e) =>
            handleTextChange("description", e.currentTarget.value)
          }
          error={fieldErrors.description}
          disabled={isBusy}
          autosize
          minRows={3}
          maxRows={6}
        />

        <StatusSelector
          label="Status"
          value={formValues.status}
          onChange={handleStatusChange}
          error={fieldErrors.status}
          disabled={isBusy}
          withIcons
        />
      </Stack>
    </AppDrawer>
  );
};
